<template>
    <div class="meta-insights-overview">
        <div class="header">
            <div class="title-block">
                <div class="title">{{ $t("metaInsights.overviewTitle") }}</div>
                <div class="subtitle" v-if="adAccount">
                    {{ adAccount.name }}
                    <span class="muted">({{ adAccount.id }})</span>
                </div>
            </div>
            <div class="toolbar">
                <DateRangePicker v-model="dateRange" @change="onDateRangeChange" />
                <SupsisButton ghost @click="loadAll" :disabled="loading">
                    <i class="material-icons" :class="{ spinning: loading }">refresh</i>
                    {{ $t("metaInsights.refresh") }}
                </SupsisButton>
            </div>
        </div>

        <!-- Account Info Banner -->
        <div class="info-banner" v-if="adAccount">
            <div class="info-item">
                <i class="material-icons">flag</i>
                <span class="label">{{ $t("metaInsights.banner.status") }}</span>
                <span class="value" :class="accountStatusClass">{{ accountStatusLabel }}</span>
            </div>
            <div class="info-item">
                <i class="material-icons">paid</i>
                <span class="label">{{ $t("metaInsights.banner.currency") }}</span>
                <span class="value">{{ adAccount.currency || "-" }}</span>
            </div>
            <div class="info-item">
                <i class="material-icons">schedule</i>
                <span class="label">{{ $t("metaInsights.banner.timezone") }}</span>
                <span class="value">{{ adAccount.timezone_name || "-" }}</span>
            </div>
            <div class="info-item" v-if="adAccount.business && adAccount.business.name">
                <i class="material-icons">business</i>
                <span class="label">{{ $t("metaInsights.banner.business") }}</span>
                <span class="value">{{ adAccount.business.name }}</span>
            </div>
        </div>

        <!-- KPI -->
        <div class="section">
            <InsightsKpiCards :insights="summary" :currency="currency" :loading="loadingSummary" />
        </div>

        <!-- Trend Chart -->
        <div class="section">
            <div class="section-title">{{ $t("metaInsights.dailyTrend") }}</div>
            <div class="chart-wrapper" v-if="trendSeries && trendSeries.length > 0">
                <ECharts :option="chartOption" class="echart-graph" />
            </div>
            <div class="chart-empty" v-else-if="!loadingTrend">
                {{ $t("metaInsights.noTrendData") }}
            </div>
            <a-skeleton v-else :active="true" />
        </div>

        <!-- Top Campaigns -->
        <div class="section">
            <div class="section-title-row">
                <div class="section-title">{{ $t("metaInsights.topCampaigns") }}</div>
                <a @click="goToCampaigns">{{ $t("metaInsights.seeAll") }}</a>
            </div>
            <a-spin :spinning="loadingCampaigns">
                <InsightsTable
                    :rows="topCampaigns"
                    :currency="currency"
                    :clickable="true"
                    table-id="overview-top-campaigns"
                    name-label-key="metaInsights.col.campaignName"
                />
            </a-spin>
        </div>

        <!-- Top Ads -->
        <div class="section">
            <div class="section-title-row">
                <div class="section-title">{{ $t("metaInsights.topAds") }}</div>
                <a @click="goToAds">{{ $t("metaInsights.seeAll") }}</a>
            </div>
            <a-spin :spinning="loadingAds">
                <InsightsTable
                    :rows="topAds"
                    :currency="currency"
                    :clickable="false"
                    :show-creative="true"
                    table-id="overview-top-ads"
                    name-label-key="metaInsights.col.adName"
                />
            </a-spin>
        </div>

        <!-- Conversions / Actions -->
        <div class="section">
            <div class="section-title">{{ $t("metaInsights.actionsTitle") }}</div>
            <div class="actions-grid" v-if="actionsRows && actionsRows.length > 0">
                <div class="action-card" v-for="row in actionsRows" :key="row.type">
                    <div class="action-label">{{ actionLabel(row.type) }}</div>
                    <div class="action-value">{{ formatNumber(row.value) }}</div>
                    <div class="action-cost" v-if="row.costPerAction != null">
                        {{ formatCurrency(row.costPerAction, currency) }}
                        <span class="muted">/ {{ $t("metaInsights.perAction") }}</span>
                    </div>
                </div>
            </div>
            <div class="empty-line" v-else-if="!loadingSummary">
                {{ $t("metaInsights.noActions") }}
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import _ from "lodash";
import { mapState } from "vuex";
import { createComponent, plugin } from "echarts-for-vue";
import * as echarts from "echarts/lib/echarts";
import "echarts/lib/component/tooltip";
import "echarts/lib/component/title";
import "echarts/lib/component/grid";
import "echarts/lib/component/legend";
import "echarts/lib/component/toolbox";
import { LineChart } from "echarts/charts";
echarts.use([LineChart]);
Vue.use(plugin, { echarts });

import DateRangePicker from "./DateRangePicker.vue";
import InsightsKpiCards from "./InsightsKpiCards.vue";
import InsightsTable from "./InsightsTable.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import { INSIGHTS_API } from "../../../../../api/meta";

const TOP_LIMIT = 5;

const ACTION_TYPE_LABELS = {
    link_click: "metaInsights.action.linkClick",
    landing_page_view: "metaInsights.action.landingPageView",
    lead: "metaInsights.action.lead",
    purchase: "metaInsights.action.purchase",
    add_to_cart: "metaInsights.action.addToCart",
    initiate_checkout: "metaInsights.action.initiateCheckout",
    onsite_conversion_messaging_conversation_started_7d: "metaInsights.action.messagingStarted",
    post_engagement: "metaInsights.action.postEngagement",
    page_engagement: "metaInsights.action.pageEngagement",
    video_view: "metaInsights.action.videoView",
};

const formatNumber = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    const num = Number(val);
    if (isNaN(num)) return val;
    return num.toLocaleString("tr-TR", { maximumFractionDigits: 2 });
};

const formatCurrency = (val, currency) => {
    if (val === null || val === undefined || val === "") return "-";
    const num = Number(val);
    if (isNaN(num)) return val;
    return num.toLocaleString("tr-TR", { maximumFractionDigits: 2 }) + " " + (currency || "");
};

export default {
    name: "Overview",
    components: {
        ECharts: createComponent({ echarts }),
        DateRangePicker,
        InsightsKpiCards,
        InsightsTable,
        SupsisButton,
    },
    props: {
        channel: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            dateRange: { datePreset: "last_30d" },
            summary: {},
            trendData: [],
            campaigns: [],
            ads: [],
            loadingSummary: false,
            loadingTrend: false,
            loadingCampaigns: false,
            loadingAds: false,
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => state.site.site && state.site.site._id,
        }),
        adAccount() {
            return _.get(this.channel, "payload.selectedAdAccount");
        },
        currency() {
            return _.get(this.channel, "payload.selectedAdAccount.currency", "");
        },
        accountStatusLabel() {
            const map = {
                1: this.$t("metaInsights.accountStatus.active"),
                2: this.$t("metaInsights.accountStatus.disabled"),
                3: this.$t("metaInsights.accountStatus.unsettled"),
                7: this.$t("metaInsights.accountStatus.pendingRiskReview"),
                8: this.$t("metaInsights.accountStatus.pendingSettlement"),
                9: this.$t("metaInsights.accountStatus.inGracePeriod"),
                100: this.$t("metaInsights.accountStatus.pendingClosure"),
                101: this.$t("metaInsights.accountStatus.closed"),
                201: this.$t("metaInsights.accountStatus.anyActive"),
                202: this.$t("metaInsights.accountStatus.anyClosed"),
            };
            const status = _.get(this.adAccount, "account_status");
            return map[status] || "-";
        },
        accountStatusClass() {
            const status = _.get(this.adAccount, "account_status");
            if (status === 1) return "ok";
            if ([2, 3, 7, 8, 100, 101].indexOf(status) >= 0) return "danger";
            return "warn";
        },
        loading() {
            return this.loadingSummary || this.loadingTrend || this.loadingCampaigns || this.loadingAds;
        },
        trendSeries() {
            if (!this.trendData || this.trendData.length === 0) return [];
            const xAxis = this.trendData.map(function (x) {
                return x.date_start;
            });
            const spend = this.trendData.map(function (x) {
                return Number(x.spend) || 0;
            });
            const impressions = this.trendData.map(function (x) {
                return Number(x.impressions) || 0;
            });
            const clicks = this.trendData.map(function (x) {
                return Number(x.clicks) || 0;
            });
            const reach = this.trendData.map(function (x) {
                return Number(x.reach) || 0;
            });
            return [
                { name: this.$t("metaInsights.kpi.spend"), data: spend, xAxis: xAxis },
                { name: this.$t("metaInsights.kpi.impressions"), data: impressions, xAxis: xAxis },
                { name: this.$t("metaInsights.kpi.clicks"), data: clicks, xAxis: xAxis },
                { name: this.$t("metaInsights.kpi.reach"), data: reach, xAxis: xAxis },
            ];
        },
        chartOption() {
            const series = this.trendSeries;
            if (!series || series.length === 0) return {};
            const xAxisData = series[0].xAxis;
            return {
                tooltip: { trigger: "axis", axisPointer: { type: "cross" } },
                legend: { data: series.map(function (s) { return s.name; }) },
                toolbox: { feature: { saveAsImage: {} } },
                grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
                xAxis: [{ type: "category", boundaryGap: false, data: xAxisData }],
                yAxis: [{ type: "value" }],
                series: series.map(function (s) {
                    return { name: s.name, type: "line", smooth: true, data: s.data };
                }),
            };
        },
        topCampaigns() {
            const list = (this.campaigns || []).slice();
            list.sort(function (a, b) {
                const sa = Number(_.get(a, "insights.data[0].spend")) || 0;
                const sb = Number(_.get(b, "insights.data[0].spend")) || 0;
                return sb - sa;
            });
            return list.slice(0, TOP_LIMIT);
        },
        topAds() {
            const list = (this.ads || []).slice();
            list.sort(function (a, b) {
                const ia = Number(_.get(a, "insights.data[0].impressions")) || 0;
                const ib = Number(_.get(b, "insights.data[0].impressions")) || 0;
                return ib - ia;
            });
            return list.slice(0, TOP_LIMIT);
        },
        actionsRows() {
            const ins = this.summary || {};
            const actions = ins.actions || [];
            const costs = ins.cost_per_action_type || [];
            const costMap = {};
            costs.forEach(function (c) {
                costMap[c.action_type] = c.value;
            });
            return actions
                .filter(function (a) {
                    return ACTION_TYPE_LABELS[a.action_type];
                })
                .map(function (a) {
                    return {
                        type: a.action_type,
                        value: a.value,
                        costPerAction: costMap[a.action_type] != null ? costMap[a.action_type] : null,
                    };
                });
        },
    },
    methods: {
        formatNumber: formatNumber,
        formatCurrency: formatCurrency,
        actionLabel(type) {
            const key = ACTION_TYPE_LABELS[type];
            return key ? this.$t(key) : type;
        },
        onDateRangeChange() {
            this.loadAll();
        },
        loadAll() {
            this.loadSummary();
            this.loadTrend();
            this.loadCampaigns();
            this.loadAds();
        },
        async loadSummary() {
            if (!this.channel) return;
            this.loadingSummary = true;
            try {
                const res = await INSIGHTS_API.getAccountInsights(this.channel._id, this.dateRange);
                const data = (res && res.data) || [];
                this.summary = data[0] || {};
            } catch (err) {
                console.error(err);
                this.$message.error(this.$t("metaInsights.failedToLoadInsights"));
                this.summary = {};
            } finally {
                this.loadingSummary = false;
            }
        },
        async loadTrend() {
            if (!this.channel) return;
            this.loadingTrend = true;
            try {
                const res = await INSIGHTS_API.getAccountInsights(
                    this.channel._id,
                    Object.assign({}, this.dateRange, { timeIncrement: "1" })
                );
                this.trendData = (res && res.data) || [];
            } catch (err) {
                console.error(err);
                this.trendData = [];
            } finally {
                this.loadingTrend = false;
            }
        },
        async loadCampaigns() {
            if (!this.channel) return;
            this.loadingCampaigns = true;
            try {
                const res = await INSIGHTS_API.listCampaigns(
                    this.channel._id,
                    Object.assign({}, this.dateRange, { limit: 50 })
                );
                this.campaigns = _.get(res, "data.data") || [];
            } catch (err) {
                console.error(err);
                this.campaigns = [];
            } finally {
                this.loadingCampaigns = false;
            }
        },
        async loadAds() {
            if (!this.channel) return;
            this.loadingAds = true;
            try {
                const res = await INSIGHTS_API.listAds(
                    this.channel._id,
                    null,
                    Object.assign({}, this.dateRange, { limit: 50 })
                );
                this.ads = _.get(res, "data.data") || [];
            } catch (err) {
                console.error(err);
                this.ads = [];
            } finally {
                this.loadingAds = false;
            }
        },
        goToCampaigns() {
            this.$emit("change-tab", "campaigns");
        },
        goToAds() {
            this.$emit("change-tab", "ads");
        },
    },
    mounted() {
        this.loadAll();
    },
};
</script>

<style scoped lang="scss">
.meta-insights-overview {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 20px;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 12px;
        padding-bottom: 14px;
        border-bottom: 1px solid var(--color-border-default);

        .title-block {
            display: flex;
            flex-direction: column;
            gap: 2px;

            .title {
                font-size: 18px;
                font-weight: 700;
                color: var(--color-text-strong-default);
            }

            .subtitle {
                font-size: 14px;
                color: var(--color-text-default);

                .muted {
                    color: var(--color-text-muted-default);
                    font-size: 12px;
                    margin-left: 6px;
                }
            }
        }

        .toolbar {
            display: flex;
            align-items: center;
            gap: 8px;
        }
    }

    .info-banner {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        padding: 12px 16px;
        background: var(--color-bg-info-subtle, #f3f8ff);
        border: 1px solid var(--color-border-info-subtle, #e0eaff);
        border-radius: 8px;

        .info-item {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-size: 13px;

            i {
                color: var(--color-text-info-default, #1f6feb);
                font-size: 16px;
            }

            .label {
                color: var(--color-text-muted-default, #6b7280);
            }

            .value {
                font-weight: 600;
                color: var(--color-text-strong-default);

                &.ok {
                    color: #389e0d;
                }
                &.warn {
                    color: #d48806;
                }
                &.danger {
                    color: #cf1322;
                }
            }
        }
    }

    .section {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .section-title {
            font-size: 14px;
            font-weight: 700;
            color: var(--color-text-strong-default);
            text-transform: uppercase;
            letter-spacing: 0.4px;
        }

        .section-title-row {
            display: flex;
            align-items: center;
            justify-content: space-between;

            a {
                font-size: 12px;
                color: #1f6feb;
            }
        }

        .chart-wrapper {
            width: 100%;
            height: 360px;
            background: var(--color-bg-default);
            border: 1px solid var(--color-border-default);
            border-radius: 8px;
            padding: 10px;

            .echart-graph {
                width: 100%;
                height: 100%;
            }
        }

        .chart-empty,
        .empty-line {
            padding: 30px;
            text-align: center;
            color: var(--color-text-muted-default);
            background: var(--color-bg-default);
            border: 1px solid var(--color-border-default);
            border-radius: 8px;
        }

        .actions-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
            gap: 12px;

            .action-card {
                display: flex;
                flex-direction: column;
                gap: 4px;
                background: var(--color-bg-default);
                border: 1px solid var(--color-border-default);
                border-radius: 8px;
                padding: 12px 14px;

                .action-label {
                    font-size: 12px;
                    color: var(--color-text-muted-default);
                    text-transform: uppercase;
                    letter-spacing: 0.4px;
                }

                .action-value {
                    font-size: 20px;
                    font-weight: 700;
                    color: var(--color-text-strong-default);
                }

                .action-cost {
                    font-size: 12px;
                    color: var(--color-text-default);

                    .muted {
                        color: var(--color-text-muted-default);
                    }
                }
            }
        }
    }

    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }

    .spinning {
        animation: spin 1s linear infinite;
    }
}
</style>
